/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { MouseEvent } from 'react';

import { Owlmoji as OwlmojiMaps } from '@readme/markdown';
import fuzzysort from 'fuzzysort';
import { forwardRef, useCallback, useState } from 'react';
import { FixedSizeGrid } from 'react-window';

import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';
import Box from '@ui/Box';
import Icon from '@ui/Icon';
import Input from '@ui/Input';
import Owlmoji from '@ui/Owlmoji';
import Segment, { SegmentItem } from '@ui/Segment';

import { brandIcons, faIcons } from './icons';
import classes from './style.module.scss';

/** @see https://docs-v5.fontawesome.com/web/reference-icons */
type IconStyle = 'brands' | 'duotone' | 'regular';
type IconStylePrefix = 'fab' | 'fad' | 'far';

const iconStyleMap = {
  brands: 'fab',
  duotone: 'fad',
  regular: 'far',
} as const satisfies Record<IconStyle, IconStylePrefix>;

interface Emoji {
  aliases: string;
  name: string;
}

interface EmojiPickerProps {
  /** The number of columns to display */
  columnCount?: number;
  /** The height of the emoji/icon results panel */
  height?: number;
  onClick?: (newValue: string, newValueType: string, event: MouseEvent) => void;
  /** Whether to include ReadMe specific Owlbert emojis */
  owlmoji?: boolean;
  /** Whether to include the icon picker */
  showIcons?: boolean;
  /** The currently selected emoji */
  value?: string;
}

const noneEmoji: Emoji = { name: 'None', aliases: 'none' };
const baseEmojis = OwlmojiMaps.owlmoji.map(emoji => {
  const [name, ...aliases] = emoji.names;
  return { name, aliases: aliases.join(', ').replace(/_/g, ' ') };
});

const emojis = [noneEmoji, ...baseEmojis];
const emojisSansOwl = [noneEmoji, ...baseEmojis.filter(({ name }) => OwlmojiMaps.kind(name) === 'gemoji')];

const noneIcon = 'None';
const icons = [noneIcon, ...faIcons, ...brandIcons];

const getButtonClasses = (name: string, selected: string | undefined, isIcon: boolean) =>
  classy(
    classes['EmojiPicker-Button'],
    isIcon && name !== 'None' && classes['EmojiPicker-Button_icon'],
    name === 'None' && classes['EmojiPicker-Button_none'],
    name === selected && classes['EmojiPicker-Button_active'],
    name === 'None' && selected === '' && classes['EmojiPicker-Button_active'],
  );

const getIconStylePrefix = (name: string, iconStyle: IconStyle): IconStylePrefix => {
  // if the icon name is a brand icon, make sure we use the `fab` style prefix
  if (brandIcons.includes(name)) {
    return iconStyleMap.brands;
  }

  // otherwise, respect the icon style that the caller asked for
  return iconStyleMap[iconStyle];
};

const renderButton = (name: string, options: { selected: string | undefined; iconStyle?: IconStyle }) => {
  const { selected, iconStyle } = options;

  const isIcon = !!iconStyle;
  const isActualIcon = isIcon && name !== noneIcon;
  const iconStylePrefix = isActualIcon ? getIconStylePrefix(name, iconStyle) : undefined;

  const composedName = iconStylePrefix ? `${iconStylePrefix} fa-${name}` : name;
  const ariaLabel = name === noneIcon ? 'Remove selected emoji or icon' : `Pick ${name} ${isIcon ? 'icon' : 'emoji'}`;

  return (
    <button
      key={name}
      aria-label={ariaLabel}
      className={getButtonClasses(composedName, selected, isIcon)}
      data-name={name === 'None' ? '' : composedName}
      data-type={isIcon ? 'icon' : 'emoji'}
      disabled={name === 'None' ? selected === '' : composedName === selected}
      type="button"
    >
      {isActualIcon ? (
        <i aria-hidden="true" className={`${classes['EmojiPicker-Icon']} ${composedName}`} />
      ) : (
        <>
          <span
            aria-hidden="true"
            className={classy(
              classes['EmojiPicker-Blur'],
              composedName === selected && classes['EmojiPicker-Blur_active'],
            )}
          >
            <Owlmoji className={classes['EmojiPicker-Blur-emoji']}>{composedName}</Owlmoji>
          </span>
          <Owlmoji aria-hidden="true" data-name={name === 'None' ? '' : composedName} data-type="emoji">
            {composedName}
          </Owlmoji>
        </>
      )}
    </button>
  );
};

/** Our inner virtual list container, mainly so we can give it a role. */
const Grid = forwardRef<HTMLDivElement>(function Grid({ ...rest }, ref) {
  return <div {...rest} ref={ref} role="grid" />;
});

const EmojiPicker = ({
  columnCount = 8,
  height = 262,
  onClick,
  owlmoji = true,
  showIcons = false,
  value,
}: EmojiPickerProps) => {
  const bem = useClassy(classes, 'EmojiPicker');
  const [selected, setSelected] = useState(value);
  const [filter, setFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleNav = (index: number) => {
    setSelectedIndex(index);
  };

  const filteredEmojis = filter
    ? fuzzysort
        .go<Emoji>(filter, owlmoji ? emojis : emojisSansOwl, {
          keys: ['name', 'aliases'],
        })
        .map(result => result.obj)
    : emojis;

  const filteredIcons = filter
    ? fuzzysort.go(filter, icons).map(result => ({ name: result.target }))
    : icons.map(icon => ({ name: icon }));

  const handleClick = useCallback(
    event => {
      if (!('name' in event.target.dataset)) return;

      const newValue = event.target.dataset.name;
      const newValueType = event.target.dataset.type;
      setSelected(newValue);

      if (typeof onClick === 'function') onClick(newValue, newValueType, event);
    },
    [onClick],
  );

  const filterInput = (placeholder: string) => (
    <Input
      aria-label={placeholder}
      autoFocus
      className={bem('&-Search')}
      onChange={e => setFilter(e.target.value.trim())}
      placeholder={placeholder}
      prefix={<Icon name="search" />}
      size="sm"
      value={filter}
      wrapperClassName={bem('&-Search-wrapper')}
    />
  );

  const emojiTab = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * columnCount + columnIndex;
    return <div style={style}>{renderButton(filteredEmojis[index]?.name, { selected })}</div>;
  };

  const iconTab = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * columnCount + columnIndex;
    return <div style={style}>{renderButton(filteredIcons[index]?.name, { selected, iconStyle: 'regular' })}</div>;
  };

  const duotoneTab = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * columnCount + columnIndex;
    return <div style={style}>{renderButton(filteredIcons[index]?.name, { selected, iconStyle: 'duotone' })}</div>;
  };

  const selectedTab = (index: number) => {
    switch (index) {
      case 0:
        return emojiTab;
      case 1:
        return iconTab;
      case 2:
        return duotoneTab;
      default:
        return emojiTab;
    }
  };

  const itemCount = selectedIndex === 0 ? filteredEmojis.length : filteredIcons.length;
  const EMOJI_ITEM_SIZE = 37;
  const EMOJI_MENU_WIDTH = columnCount * EMOJI_ITEM_SIZE + 24; // 37px per column + 20px padding + 4px emoji margin offset

  return (
    <Box className={bem('&')} kind="pop" style={{ width: `${EMOJI_MENU_WIDTH}px`, overflow: 'hidden', padding: 0 }}>
      {!!showIcons && (
        <div className={bem('&-Tabs')}>
          <Segment kind="primary" selectedIndex={0} size="sm" underline>
            <SegmentItem onClick={() => handleNav(0)}>Emoji</SegmentItem>
            <SegmentItem onClick={() => handleNav(1)}>Icons</SegmentItem>
            <SegmentItem onClick={() => handleNav(2)}>Duotone</SegmentItem>
          </Segment>
        </div>
      )}
      <div className={bem('&-Tab')} onClick={handleClick}>
        {filterInput(`${selectedIndex === 0 ? 'Search emojis' : 'Search icons'}`)}
        <FixedSizeGrid
          className={bem('&-Results')}
          columnCount={columnCount}
          columnWidth={EMOJI_ITEM_SIZE}
          height={height}
          innerElementType={Grid}
          rowCount={itemCount / columnCount}
          rowHeight={EMOJI_ITEM_SIZE}
          width={EMOJI_MENU_WIDTH}
        >
          {selectedTab(selectedIndex)}
        </FixedSizeGrid>
      </div>
    </Box>
  );
};

export default EmojiPicker;
